<!--
  TypeSelect.vue

  A select dropdown for choosing record types.
  Uses the types store to populate options.

  PROPS:
    - modelValue: String - The selected type name (v-model)
    - showAll: Boolean - Whether to show an "All types" option (default: false)
    - allowAny: Boolean - Offer "Any type", which selects the empty value (default: false)
    - placeholder: String - Placeholder when no selection (default: "Select type...")
    - excludeList: Boolean - Whether to exclude the "list" type (default: false)
    - usePluralLabel: Boolean - Use plural_label instead of label (default: false)
    - showIcon: Boolean - Show icon for selected type (default: false)
    - size: String - Size class (small, normal, medium, large)

  EVENTS:
    - @update:modelValue - Emitted when selection changes

  USAGE:
    <TypeSelect v-model="selectedType" />
    <TypeSelect v-model="selectedType" size="small" />
    <TypeSelect v-model="selectedType" show-all />
    <TypeSelect v-model="selectedType" exclude-list />
    <TypeSelect v-model="selectedType" allow-any />
    <TypeSelect v-model="selectedType" show-all use-plural-label show-icon size="small" />
-->
<template>
  <div class="control type-select" :class="{ 'has-icons-left': showIcon }">
    <span v-if="showIcon" class="icon ml-1 pr-2" :class="sizeClass" style="height: 100%">
      <font-awesome-icon :size="iconSize" :icon="selectedIcon" />
    </span>
    <div class="select" :class="sizeClass">
      <select
        :value="modelValue ?? ''"
        @change="$emit('update:modelValue', ($event.target as HTMLSelectElement).value)"
      >
        <option v-if="showAll" value="all">All Types</option>
        <option v-else-if="allowAny" value="">Any type</option>
        <option v-else-if="!modelValue" value="" disabled>{{ placeholder }}</option>
        <option v-if="showAll && availableTypes.length > 0" disabled>──────</option>
        <option v-for="type in availableTypes" :key="type.name" :value="type.name">
          {{ usePluralLabel ? type.plural_label : type.label }}
        </option>
      </select>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue"
import { getTypeIcon, type RecordType, useTypesStore } from "@/stores/types"

const props = withDefaults(
  defineProps<{
    modelValue?: string | null
    showAll?: boolean
    allowAny?: boolean
    placeholder?: string
    excludeList?: boolean
    allowedTypes?: string[] | null
    usePluralLabel?: boolean
    showIcon?: boolean
    size?: "small" | "normal" | "medium" | "large"
  }>(),
  {
    modelValue: "",
    showAll: false,
    allowAny: false,
    placeholder: "Select type...",
    excludeList: false,
    allowedTypes: null,
    usePluralLabel: false,
    showIcon: true,
    size: "normal",
  }
)

defineEmits<{
  "update:modelValue": [value: string]
}>()

const typesStore = useTypesStore()

const availableTypes = computed<RecordType[]>(() => {
  const base = props.excludeList ? typesStore.entityTypes : typesStore.types
  if (props.allowedTypes) {
    return base.filter((t) => props.allowedTypes!.includes(t.name))
  }
  return base
})

const selectedIcon = computed(() => {
  if (!props.modelValue || props.modelValue === "all") {
    return "list"
  }
  return getTypeIcon(props.modelValue)
})

const sizeClass = computed(() => {
  if (!props.size) return null
  return `is-${props.size}`
})

//   size?: '2xs' | 'xs' | 'sm' | 'lg' | 'xl' | '2xl' | '1x' | '2x' | '3x'

const iconSize = computed(() => {
  switch (props.size) {
    case "small":
      return "sm"
    case "normal":
      return "lg"
    case "medium":
      return "xl"
    case "large":
      return "2x"
    default:
      return "lg"
  }
})
</script>
